// src/pages/Amenities.js
import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import images from '../assets/images';
import { sendPartyHallEnquiry } from '../services/whatsappService';


import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';

import {
  Pool,
  Spa,
  LocalBar,
} from '@mui/icons-material';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
  },
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  overflow: 'hidden',
}));

const EnquiryDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    padding: theme.spacing(2),
    background: 'linear-gradient(to right, #ffffff, #f8f9fa)',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '50px',
  padding: '12px 32px',
  fontSize: '1.1rem',
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(46, 125, 50, 0.25)',
  },
  transition: 'all 0.3s ease',
}));

// Data
const amenities = [
  {
    id: 1,
    title: 'Pool',
    category: 'Pools & Beach',
    icon: <Pool fontSize="large" />,
    image: images.pool,
    description: 'Experience tranquility in our stunning pool.',
    features: [
      'Temperature-controlled water',
      'Poolside service',
      'Kids pool area',
    ],
    hours: '7:00 AM - 7:00 PM',
  },
  {
    id: 2,
    title: 'Badminton Court',
    category: 'Sports',
    icon: <Spa fontSize="large" />,
    image: images.badminton,
    description: 'Badminton courts for players of all levels.',
    features: [
      'Badminton Kit',
      'Court Net',
    ],
    hours: '9:00 AM - 8:00 PM',
  },
  {
    id: 3,
    title: 'Party Spaces',
    category: 'Events',
    icon: <LocalBar fontSize="large" />,
    image: images.party,
    description: 'A stylish party hall for events—all in one place.',
    features: [
      'Party Halls',
      'Event Planning',
      '50 - 100 people events'
    ],
    hours: '10:00 AM - 5:00 PM',
  },
];

const categories = ['All'];

// AmenityDialog Component
function AmenityDialog({ amenity, open, onClose }) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 600,
        fontSize: '1.5rem',
        borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: 3
      }}>
        {amenity.title}
      </DialogTitle>
      <DialogContent sx={{ pt: 3, px: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <CardMedia
              component="img"
              height="400"
              image={amenity.image}
              alt={amenity.title}
              sx={{ 
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom color="primary">
              About
            </Typography>
            <Typography variant="body1" paragraph>
              {amenity.description}
            </Typography>
            <Typography variant="h6" gutterBottom color="primary">
              Features
            </Typography>
            <Box sx={{ pl: 2 }}>
              {amenity.features.map((feature) => (
                <Typography 
                  key={feature} 
                  variant="body1" 
                  sx={{ 
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    '&:before': {
                      content: '"•"',
                      color: 'primary.main',
                      fontWeight: 'bold',
                      fontSize: '1.5rem',
                      marginRight: '8px'
                    }
                  }}
                >
                  {feature}
                </Typography>
              ))}
            </Box>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
              Hours of Operation
            </Typography>
            <Typography variant="body1">
              {amenity.hours}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <StyledButton 
          onClick={onClose}
          variant="contained"
        >
          Close
        </StyledButton>
      </DialogActions>
    </Dialog>
  );
}
// Party Hall Section Component
function PartyHallSection() {
  const [openEnquiry, setOpenEnquiry] = useState(false);
  const [enquiryData, setEnquiryData] = useState({
    name: '',
    phone: '',
    email: '',
    date: null,
    guestCount: '',
    eventType: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleEnquirySubmit = async () => {
    if (!enquiryData.name || !enquiryData.phone || !enquiryData.date) {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'error',
      });
      return;
    }
  
    try {
      setLoading(true);
  
      const notificationResult = await sendPartyHallEnquiry(enquiryData);
  
      if (notificationResult.success) {
        setSnackbar({
          open: true,
          message: 'Enquiry sent successfully! We will contact you shortly.',
          severity: 'success',
        });
        setOpenEnquiry(false);
        // Reset form
        setEnquiryData({
          name: '',
          phone: '',
          email: '',
          date: null,
          guestCount: '',
          eventType: '',
          message: '',
        });
      } else {
        throw new Error(notificationResult.error || 'Failed to send enquiry');
      }
    } catch (error) {
      console.error('Enquiry submission error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send enquiry. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 8, mb: 4 }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              borderRadius: '24px',
              padding: 4,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Book Our Party Hall
                </Typography>
                <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
                  Perfect for Your Special Occasions
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
                  Available from 10:00 AM to 5:00 PM
                  <br />
                  Capacity: 50-100 people
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <StyledButton
                    variant="contained"
                    color="secondary"
                    onClick={() => setOpenEnquiry(true)}
                    sx={{
                      backgroundColor: 'white',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.9)',
                      },
                    }}
                  >
                    Make an Enquiry
                  </StyledButton>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src={images.party}
                  alt="Party Hall"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </motion.div>

        {/* Enquiry Dialog */}
        <EnquiryDialog
          open={openEnquiry}
          onClose={() => setOpenEnquiry(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            textAlign: 'center', 
            fontSize: '1.5rem', 
            fontWeight: 'bold',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            pb: 2
          }}>
            Party Hall Enquiry
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Name"
                  required
                  value={enquiryData.name}
                  onChange={(e) => setEnquiryData({ ...enquiryData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Phone"
                  required
                  value={enquiryData.phone}
                  onChange={(e) => setEnquiryData({ ...enquiryData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Email (Optional)"
                  type="email"
                  value={enquiryData.email}
                  onChange={(e) => setEnquiryData({ ...enquiryData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Event Date"
                    value={enquiryData.date}
                    onChange={(newValue) => setEnquiryData({ ...enquiryData, date: newValue })}
                    renderInput={(params) => 
                      <StyledTextField {...params} fullWidth required />
                    }
                    minDate={new Date()}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledTextField
                  fullWidth
                  label="Number of Guests"
                  type="number"
                  value={enquiryData.guestCount}
                  onChange={(e) => setEnquiryData({ ...enquiryData, guestCount: e.target.value })}
                  InputProps={{ inputProps: { min: 1, max: 100 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Event Type"
                  value={enquiryData.eventType}
                  onChange={(e) => setEnquiryData({ ...enquiryData, eventType: e.target.value })}
                  placeholder="e.g., Birthday Party, Corporate Event"
                />
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Additional Message"
                  multiline
                  rows={4}
                  value={enquiryData.message}
                  onChange={(e) => setEnquiryData({ ...enquiryData, message: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ padding: 3, gap: 2 }}>
            <StyledButton
              onClick={() => setOpenEnquiry(false)}
              variant="outlined"
            >
              Cancel
            </StyledButton>
            <StyledButton
              onClick={handleEnquirySubmit}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Enquiry'}
            </StyledButton>
          </DialogActions>
        </EnquiryDialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

// Main Amenities Component
function Amenities() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedAmenity, setSelectedAmenity] = useState(null);

  const filteredAmenities = selectedCategory === 'All'
    ? amenities
    : amenities.filter(amenity => amenity.category === selectedCategory);

  return (
    <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Typography 
            variant="h3" 
            component="h1" 
            textAlign="center" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(45deg, #1a237e 30%, #0d47a1 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Resort Amenities
          </Typography>
          <Typography 
            variant="h6" 
            textAlign="center" 
            color="text.secondary" 
            sx={{ mb: 6 }}
          >
            Discover our facilities and services
          </Typography>
        </motion.div>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 6 }}>
          <Tabs
            value={selectedCategory}
            onChange={(e, newValue) => setSelectedCategory(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            centered
          >
            {categories.map((category) => (
              <Tab 
                key={category} 
                label={category} 
                value={category}
                sx={{
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                }}
              />
            ))}
          </Tabs>
        </Box>

        <Grid container spacing={4}>
          {filteredAmenities.map((amenity) => (
            <Grid item key={amenity.id} xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <StyledCard>
                  <CardMedia
                    component="img"
                    height="240"
                    image={amenity.image}
                    alt={amenity.title}
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      {amenity.icon}
                      <Typography variant="h6" component="h2" sx={{ ml: 1 }}>
                        {amenity.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {amenity.description}
                    </Typography>
                    <StyledButton
                      variant="outlined"
                      color="primary"
                      onClick={() => setSelectedAmenity(amenity)}
                      fullWidth
                    >
                      Learn More
                    </StyledButton>
                  </CardContent>
                </StyledCard>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Party Hall Section */}
        <PartyHallSection />

        {/* Amenity Dialog */}
        {selectedAmenity && (
          <AmenityDialog
            amenity={selectedAmenity}
            open={Boolean(selectedAmenity)}
            onClose={() => setSelectedAmenity(null)}
          />
        )}
      </Container>
    </Box>
  );
}

export default Amenities;